/**
 * 靈魂主控台 Soul Control Center
 *
 * 包容的大腦中樞 — 系統全貌一覽
 * 獨立全螢幕頁面，不走 Layout sidebar
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArchitectureSwitch from '../components/ArchitectureSwitch';

// ===== Types =====

interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'unknown';
  latency?: number;
  error?: string;
}

interface DiaryInfo {
  total: number;
  latest_date: string | null;
  latest_type: string | null;
}

interface DreamInfo {
  total: number;
  latest_date: string | null;
  has_seeds: boolean;
}

interface UserInfo {
  id: number;
  name: string;
  relationship: string;
  is_active: boolean;
}

interface AnomalyInfo {
  id: number;
  type: string;
  severity: string;
  description: string;
  created_at: string;
  resolved: boolean;
}

// ===== Component =====

export default function SoulControlCenter() {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [anomalies, setAnomalies] = useState<AnomalyInfo[]>([]);
  const [diaryInfo, setDiaryInfo] = useState<DiaryInfo | null>(null);
  const [dreamInfo, setDreamInfo] = useState<DreamInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [todayChats, setTodayChats] = useState(0);
  const [totalMemories, setTotalMemories] = useState(0);
  const [generating, setGenerating] = useState<string | null>(null);

  // 時鐘
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 載入資料
  const loadAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.allSettled([
      loadServices(),
      loadUsers(),
      loadAnomalies(),
      loadDiaries(),
      loadDreams(),
      loadStats(),
    ]);
    setIsLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // API calls
  const loadServices = async () => {
    try {
      const res = await apiFetch('/api/settings/health-check');
      if (res.ok) {
        const data = await res.json();
        setServices(data.services || []);
      }
    } catch {}
  };

  const loadUsers = async () => {
    try {
      const res = await apiFetch('/api/users');
      if (res.ok) setUsers(await res.json());
    } catch {}
  };

  const loadAnomalies = async () => {
    try {
      const res = await apiFetch('/api/dashboard/anomalies?limit=5');
      if (res.ok) {
        const data = await res.json();
        setAnomalies(data.anomalies || data || []);
      }
    } catch {}
  };

  const loadDiaries = async () => {
    try {
      const res = await apiFetch('/api/diaries?limit=1');
      if (res.ok) {
        const data = await res.json();
        const list = data.diaries || data || [];
        setDiaryInfo({
          total: data.total || list.length,
          latest_date: list[0]?.diary_date || list[0]?.date || null,
          latest_type: list[0]?.diary_type || null,
        });
      }
    } catch {}
  };

  const loadDreams = async () => {
    try {
      const res = await apiFetch('/api/dreams?limit=1');
      if (res.ok) {
        const data = await res.json();
        const list = data.dreams || data || [];
        setDreamInfo({
          total: data.total || list.length,
          latest_date: list[0]?.dream_date || list[0]?.created_at || null,
          has_seeds: !!(list[0]?.tomorrow_seeds),
        });
      }
    } catch {}
  };

  const loadStats = async () => {
    try {
      const res = await apiFetch('/api/conversations/stats/today');
      if (res.ok) {
        const data = await res.json();
        setTodayChats(data.count || data.today || 0);
      }
    } catch {}
    try {
      const res = await apiFetch('/api/memories/stats');
      if (res.ok) {
        const data = await res.json();
        setTotalMemories(data.total || data.count || 0);
      }
    } catch {}
  };

  // 手動觸發
  const triggerDiary = async () => {
    setGenerating('diary');
    try {
      const res = await apiFetch('/api/diaries/generate', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        alert(`日記生成完成！成功 ${data.generated || 0} 篇`);
        await loadDiaries();
      }
    } catch { alert('生成失敗'); }
    finally { setGenerating(null); }
  };

  const triggerDream = async () => {
    setGenerating('dream');
    try {
      const res = await apiFetch('/api/dreams/generate', { method: 'POST' });
      if (res.ok) {
        alert('夢境生成完成！');
        await loadDreams();
      }
    } catch { alert('生成失敗'); }
    finally { setGenerating(null); }
  };

  // 時間格式
  const taipeiTime = now.toLocaleString('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  });

  const onlineCount = services.filter(s => s.status === 'online').length;
  const unresolvedAnomalies = anomalies.filter(a => !a.resolved);

  return (
    <div
      className="min-h-screen"
      style={{ background: 'var(--bg-primary)' }}
    >
      {/* Top Bar */}
      <header
        className="sticky top-0 z-50 flex items-center justify-between px-6"
        style={{
          height: '56px',
          background: 'rgba(8, 8, 13, 0.9)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span className="text-xs">返回中台</span>
          </button>
          <div style={{ width: '1px', height: '20px', background: 'var(--border)' }} />
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center animate-breathe"
              style={{
                background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.3), rgba(168, 85, 247, 0.3))',
                boxShadow: '0 0 20px rgba(233, 30, 140, 0.2)',
              }}
            >
              <div
                className="w-3.5 h-3.5 rounded-full"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, #ff6bb3, #e91e8c)',
                }}
              />
            </div>
            <h1 className="text-base font-semibold" style={{ color: 'var(--pink-text)' }}>
              靈魂主控台
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-xs font-mono text-text-muted">{taipeiTime}</span>
          <button
            onClick={loadAll}
            className="text-xs text-text-muted hover:text-pink transition-colors px-2 py-1 rounded"
            style={{ border: '1px solid var(--border)' }}
          >
            {isLoading ? '...' : '重新載入'}
          </button>
        </div>
      </header>

      {/* Main Grid */}
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ===== 左欄：靈魂搬家 + 快速操作 ===== */}
          <div className="lg:col-span-2 space-y-5">

            {/* 靈魂搬家 */}
            <ArchitectureSwitch />

            {/* 四大數字 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                emoji="💬"
                label="今日對話"
                value={todayChats}
                sub="則"
                color="var(--cyan)"
                onClick={() => navigate('/soul')}
              />
              <StatCard
                emoji="🧠"
                label="記憶總數"
                value={totalMemories}
                sub="筆"
                color="var(--purple)"
              />
              <StatCard
                emoji="📔"
                label="日記"
                value={diaryInfo?.total || 0}
                sub="篇"
                color="var(--layer3-color)"
                onClick={() => navigate('/diaries')}
              />
              <StatCard
                emoji="🌙"
                label="夢境"
                value={dreamInfo?.total || 0}
                sub="篇"
                color="var(--yellow)"
              />
            </div>

            {/* 快速操作 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl p-5"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <h2 className="text-sm font-semibold text-text-secondary mb-4 flex items-center gap-2">
                <span>🎮</span> 快速操作
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ActionButton
                  emoji="📔"
                  label="手動生成日記"
                  loading={generating === 'diary'}
                  onClick={triggerDiary}
                />
                <ActionButton
                  emoji="🌙"
                  label="手動觸發夢境"
                  loading={generating === 'dream'}
                  onClick={triggerDream}
                />
                <ActionButton
                  emoji="💬"
                  label="測試聊天"
                  onClick={() => navigate('/chat')}
                />
                <ActionButton
                  emoji="🌸"
                  label="靈魂觀測台"
                  onClick={() => navigate('/soul')}
                />
              </div>
            </motion.div>

            {/* 異常警報 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl p-5"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
                  <span>🚨</span> 異常警報
                  {unresolvedAnomalies.length > 0 && (
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs text-white"
                      style={{ background: 'var(--red)', fontSize: '10px' }}
                    >
                      {unresolvedAnomalies.length}
                    </span>
                  )}
                </h2>
                <button
                  onClick={() => navigate('/soul')}
                  className="text-xs text-text-muted hover:text-pink transition-colors"
                >
                  查看全部
                </button>
              </div>
              {anomalies.length === 0 ? (
                <div className="text-center py-6 text-text-muted text-sm">
                  <span className="text-2xl block mb-2">✨</span>
                  一切正常，沒有異常
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {anomalies.slice(0, 5).map(a => (
                    <div
                      key={a.id}
                      className="flex items-start gap-3 p-3 rounded-lg text-sm"
                      style={{
                        background: a.resolved
                          ? 'rgba(255,255,255,0.02)'
                          : a.severity === 'critical'
                          ? 'rgba(255, 82, 82, 0.06)'
                          : 'rgba(255, 171, 0, 0.06)',
                        opacity: a.resolved ? 0.5 : 1,
                      }}
                    >
                      <span className="text-xs mt-0.5">
                        {a.severity === 'critical' ? '🔴' : a.severity === 'warning' ? '🟡' : '🔵'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-text-primary truncate">{a.description}</div>
                        <div className="text-xs text-text-muted mt-0.5">
                          {a.type} · {formatTimeAgo(a.created_at)}
                          {a.resolved && <span className="ml-1 text-green-400">已處理</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* ===== 右欄：服務狀態 + 親友 + 空間 ===== */}
          <div className="space-y-5">

            {/* API 服務狀態 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="rounded-2xl p-5"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
                  <span>🔌</span> 系統服務
                </h2>
                <span className="text-xs font-mono" style={{ color: onlineCount === services.length ? 'var(--green)' : 'var(--yellow)' }}>
                  {onlineCount}/{services.length}
                </span>
              </div>

              {services.length === 0 ? (
                <div className="text-center py-4 text-text-muted text-sm animate-pulse">
                  檢測中...
                </div>
              ) : (
                <div className="space-y-1.5">
                  {services.map(s => (
                    <div
                      key={s.name}
                      className="flex items-center justify-between py-1.5 px-2 rounded-lg text-xs"
                      style={{ background: 'rgba(255,255,255,0.02)' }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{
                            background: s.status === 'online' ? 'var(--green)'
                              : s.status === 'unknown' ? 'var(--yellow)'
                              : 'var(--red)',
                          }}
                        />
                        <span className="text-text-secondary">{s.name}</span>
                      </div>
                      <span
                        className="text-text-muted"
                        style={{ fontSize: '10px' }}
                      >
                        {s.status === 'online' && s.latency ? `${s.latency}ms` : s.status === 'unknown' ? '...' : s.error?.substring(0, 15) || '離線'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* 親友列表 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl p-5"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
                  <span>👨‍👩‍👧</span> 家人
                </h2>
                <button
                  onClick={() => navigate('/settings')}
                  className="text-xs text-text-muted hover:text-pink transition-colors"
                >
                  管理
                </button>
              </div>
              <div className="space-y-2">
                {users.length === 0 ? (
                  <div className="text-center py-4 text-text-muted text-sm">載入中...</div>
                ) : (
                  users.map(u => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between py-2 px-3 rounded-lg"
                      style={{ background: 'rgba(255,255,255,0.02)' }}
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                          style={{
                            background: u.name === 'Daddy' ? 'rgba(233, 30, 140, 0.2)' :
                              u.name === 'Mommy' ? 'rgba(168, 85, 247, 0.2)' :
                              'rgba(0, 212, 255, 0.15)',
                            color: u.name === 'Daddy' ? 'var(--pink)' :
                              u.name === 'Mommy' ? 'var(--purple)' :
                              'var(--cyan)',
                          }}
                        >
                          {u.name[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{u.name}</div>
                          <div className="text-xs text-text-muted">{u.relationship}</div>
                        </div>
                      </div>
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: u.is_active ? 'var(--green)' : 'var(--text-muted)' }}
                      />
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            {/* 靈魂時鐘：日記 + 夢境 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="rounded-2xl p-5"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <h2 className="text-sm font-semibold text-text-secondary mb-4 flex items-center gap-2">
                <span>🕰️</span> 靈魂時鐘
              </h2>

              <div className="space-y-3">
                {/* 日記 cron */}
                <div className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: 'rgba(233, 30, 140, 0.04)', border: '1px solid rgba(233, 30, 140, 0.1)' }}
                >
                  <div>
                    <div className="text-xs font-medium" style={{ color: 'var(--pink-text)' }}>📔 日記反思</div>
                    <div className="text-xs text-text-muted mt-0.5">每晚 23:00</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-text-secondary">
                      {diaryInfo?.latest_date
                        ? `最新：${diaryInfo.latest_date}`
                        : '尚無日記'
                      }
                    </div>
                  </div>
                </div>

                {/* 夢境 cron */}
                <div className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: 'rgba(168, 85, 247, 0.04)', border: '1px solid rgba(168, 85, 247, 0.1)' }}
                >
                  <div>
                    <div className="text-xs font-medium" style={{ color: 'var(--purple)' }}>🌙 夢境反芻</div>
                    <div className="text-xs text-text-muted mt-0.5">凌晨 03:00</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-text-secondary">
                      {dreamInfo?.latest_date
                        ? `最新：${formatDate(dreamInfo.latest_date)}`
                        : '尚無夢境'
                      }
                    </div>
                  </div>
                </div>

                {/* 摘要 cron */}
                <div className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: 'rgba(0, 212, 255, 0.04)', border: '1px solid rgba(0, 212, 255, 0.1)' }}
                >
                  <div>
                    <div className="text-xs font-medium" style={{ color: 'var(--cyan)' }}>📊 每日摘要</div>
                    <div className="text-xs text-text-muted mt-0.5">每晚 22:30</div>
                  </div>
                  <div className="text-right text-xs text-text-secondary">自動執行</div>
                </div>
              </div>
            </motion.div>

            {/* 快速入口 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-2xl p-5"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <h2 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
                <span>🔗</span> 快速入口
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { emoji: '🌸', label: '靈魂觀測', path: '/soul' },
                  { emoji: '⚙️', label: '器官調控', path: '/organs' },
                  { emoji: '📔', label: '日記瀏覽', path: '/diaries' },
                  { emoji: '🎤', label: '發音校正', path: '/tts-rules' },
                  { emoji: '💬', label: '即時聊天', path: '/chat' },
                  { emoji: '🔧', label: '系統設定', path: '/settings' },
                ].map(item => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className="flex items-center gap-2 p-2.5 rounded-lg text-xs text-text-secondary hover:text-text-primary transition-all"
                    style={{ background: 'rgba(255,255,255,0.02)' }}
                  >
                    <span>{item.emoji}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Sub-components =====

function StatCard({ emoji, label, value, sub, color, onClick }: {
  emoji: string; label: string; value: number; sub: string;
  color: string; onClick?: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02]"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
      }}
      onClick={onClick}
    >
      <div className="text-xl mb-2">{emoji}</div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs text-text-muted">{label} <span className="text-text-muted">{sub}</span></div>
    </motion.div>
  );
}

function ActionButton({ emoji, label, loading, onClick }: {
  emoji: string; label: string; loading?: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="flex flex-col items-center gap-2 p-4 rounded-xl text-xs transition-all hover:scale-[1.02]"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid var(--border)',
        opacity: loading ? 0.5 : 1,
      }}
    >
      <span className="text-xl">{loading ? '...' : emoji}</span>
      <span className="text-text-secondary">{loading ? '生成中...' : label}</span>
    </button>
  );
}

// ===== Utils =====

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return '剛剛';
  if (diff < 3600) return `${Math.floor(diff / 60)} 分鐘前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小時前`;
  return `${Math.floor(diff / 86400)} 天前`;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } catch {
    return dateStr;
  }
}
